import {
  Alert,
  AlertType,
  Checkbox,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Input,
  InputVariant,
  LinkButton,
  RadioGroup,
  Text,
  TextHighlight,
  TextSize,
  TextWeight,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import { FC, Fragment, useState } from 'react'
import { KpiMetricType } from '../../../data/gql-gen/questionnaire/graphql'
import { useHasEarlierWaves } from './HasEarlierWavesContext'
import {
  KpiBaseField,
  KpiTickFields,
  METRIC_HEADING,
  getKpiFootnote,
  getMetricName
} from './KpiMeasureFields'
import useRowsStyles from './KpiRowsDialog.styles'
import useStyles from './KpiSettingsDialog.styles'
import { KPI_LABEL_MAX_LENGTH, rowKpiLabel } from './kpiDialogModel'
import {
  KpiRowsDialogModel,
  KpiRowsDialogRow,
  KpiRowsSaveInput,
  canSaveKpiRows,
  getDuplicateKpiRows,
  getKpiRowsDraftLabel,
  initialKpiRowsDraft,
  setAllKpiRows,
  setKpiRowsDraftLabel,
  setKpiRowsDraftMetric,
  toKpiRowsSaveInput,
  toggleKpiRow
} from './kpiRowsDialogModel'
import { joinAnd } from './kpiRuleText'

const getSaveText = (count: number) => {
  if (count === 0) {
    return 'Mark rows'
  }
  return count === 1 ? 'Mark 1 row' : `Mark ${count} rows`
}

const getRowHint = (row: KpiRowsDialogRow) => {
  if (!row.hasTrackingId) {
    return 'No Tracking ID'
  }
  if (row.kpis.length === 0) {
    return null
  }
  return row.kpis.length === 1 ? 'Has a KPI' : `Has ${row.kpis.length} KPIs`
}

export const getDuplicateRowsMessage = (rows: KpiRowsDialogRow[]) => {
  const names = joinAnd(rows.map(({ label }) => `"${label}"`))
  return rows.length === 1
    ? `The row ${names} already has a KPI that counts exactly this. Untick it to mark the other rows.`
    : `The rows ${names} already have a KPI that counts exactly this. Untick them to mark the other rows.`
}

interface Props {
  model: KpiRowsDialogModel
  saving: boolean
  onClose: () => void
  onSave: (input: KpiRowsSaveInput) => Promise<boolean>
}

const KpiRowsDialog: FC<Props> = ({ model, saving, onClose, onSave }) => {
  const { classes } = useStyles()
  const { classes: rowsClasses, cx: classNames } = useRowsStyles()
  const { classes: textClasses } = textStyleUtils.useTextStyles()
  const hasEarlierWaves = useHasEarlierWaves()
  const [draft, setDraft] = useState(() => initialKpiRowsDraft(model))

  const saveInput = toKpiRowsSaveInput(model, draft)
  const duplicates = getDuplicateKpiRows(model, draft)
  const markableCount = model.rows.filter((row) => row.hasTrackingId).length
  const allTicked = saveInput.rowIds.length === markableCount
  const hasUntrackedRows = markableCount < model.rows.length

  return (
    <Dialog
      open
      title="Mark rows as KPIs"
      onClose={onClose}
      fullWidth
      maxWidth={DialogMaxWidth.Md}
      variant={DialogVariant.DoubleButton}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
      primaryButtonText={getSaveText(saveInput.rowIds.length)}
      primaryButtonClick={() => onSave(saveInput)}
      primaryButtonDisabled={!canSaveKpiRows(model, draft)}
      primaryButtonLoading={saving}
    >
      <div className={classes.body}>
        <Text size={TextSize.ms} highlight={TextHighlight.Background}>
          {model.contextLine}
        </Text>

        {model.metricTypes.length > 1 ? (
          <RadioGroup
            caption={METRIC_HEADING}
            name="kpi-metric"
            value={draft.metricType}
            onChange={(_, value) =>
              setDraft((current) =>
                setKpiRowsDraftMetric(current, value as KpiMetricType)
              )
            }
            items={model.metricTypes.map((metricType) => ({
              value: metricType,
              label: getMetricName(metricType, false)
            }))}
          />
        ) : (
          <div className={classes.section}>
            <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
              {METRIC_HEADING}
            </Text>
            <Text size={TextSize.ms}>
              {getMetricName(draft.metricType, false)}
            </Text>
          </div>
        )}

        <KpiTickFields
          subjectKind={model.subjectKind}
          options={model.options}
          draft={draft}
          heading="Answer options that count in each row"
          onChange={setDraft}
        />

        <KpiBaseField subject="row" draft={draft} onChange={setDraft} />

        <div className={classes.section}>
          <div className={rowsClasses.rowsGrid}>
            <div className={rowsClasses.rowsHeader}>
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                Rows to mark
              </Text>
              <LinkButton
                underlined
                noPadding
                onClick={() =>
                  setDraft((current) =>
                    setAllKpiRows(model, current, !allTicked)
                  )
                }
              >
                {allTicked ? 'Select none' : 'Select all'}
              </LinkButton>
            </div>
            <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
              KPI name
            </Text>
          </div>
          <div
            role="group"
            aria-label="Rows to mark"
            className={classNames(rowsClasses.rowsGrid, rowsClasses.rows)}
          >
            {model.rows.map((row) => {
              const hint = getRowHint(row)
              const isTicked = saveInput.rowIds.includes(row.id)
              return (
                <Fragment key={row.id}>
                  <Checkbox
                    ariaLabel={row.label}
                    // Checkbox wraps its label in a <p>, so this markup stays inline.
                    label={
                      <span className={rowsClasses.rowLabel}>
                        {row.label}
                        {hint && (
                          <span
                            className={classNames(
                              textClasses.sizeS,
                              textClasses.highlightBackground
                            )}
                          >
                            {hint}
                          </span>
                        )}
                      </span>
                    }
                    disabled={!row.hasTrackingId}
                    checked={isTicked}
                    onChange={() =>
                      setDraft((current) => toggleKpiRow(current, row.id))
                    }
                  />
                  {row.hasTrackingId ? (
                    <Input
                      variant={InputVariant.Filled}
                      fullWidth
                      ariaLabel={`KPI name for ${row.label}`}
                      value={getKpiRowsDraftLabel(draft, row)}
                      placeholder={rowKpiLabel(row.label, draft.metricType)}
                      maxLength={KPI_LABEL_MAX_LENGTH}
                      disabled={!isTicked}
                      onChange={(event) => {
                        const { value } = event.target
                        setDraft((current) =>
                          setKpiRowsDraftLabel(current, row.id, value)
                        )
                      }}
                    />
                  ) : (
                    <span />
                  )}
                </Fragment>
              )
            })}
          </div>
          <Text size={TextSize.s} highlight={TextHighlight.Background}>
            {
              "Each name is shown on the card and, later, in KPI results. A name left blank is the row's text."
            }
          </Text>
          {hasUntrackedRows && (
            <Text size={TextSize.s} highlight={TextHighlight.Background}>
              {
                "Rows without a Tracking ID can't be KPIs: a KPI follows its row from wave to wave by Tracking ID."
              }
            </Text>
          )}
        </div>

        {duplicates.length > 0 && (
          <Alert severity={AlertType.WARNING}>
            {getDuplicateRowsMessage(duplicates)}
          </Alert>
        )}

        <Text size={TextSize.s} highlight={TextHighlight.Background}>
          {getKpiFootnote(model, hasEarlierWaves, 'KPIs')}
        </Text>
      </div>
    </Dialog>
  )
}

export default KpiRowsDialog
